import React, { useState } from 'react';
import { Package, Clock, CheckCircle2, AlertCircle, ArrowRight, FileText, Video, History, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { usePackages, ReportPackage } from '../../contexts/PackagesContext';
import { ZodiacTheme } from './zodiacThemes';
interface PackagesTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
export function PackagesTab({
  theme,
  onNavigate
}: PackagesTabProps) {
  const {
    packages,
    activePackages,
    totalRemaining,
    totalUsed
  } = usePackages();
  const [expandedPackageId, setExpandedPackageId] = useState<string | null>(null);
  const toggleExpand = (id: string) => {
    setExpandedPackageId(expandedPackageId === id ? null : id);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formatPrice = (price: number) => {
    return price.toLocaleString('ru-RU') + ' ₽';
  };
  const getStatusBadge = (status: ReportPackage['status']) => {
    switch (status) {
      case 'active':
        return <Badge variant="success" data-id="element-19438">Активен</Badge>;
      case 'exhausted':
        return <Badge variant="neutral" data-id="element-19439">Закончился</Badge>;
      case 'frozen':
        return <Badge variant="warning" data-id="element-19440">Заморожен</Badge>;
      case 'cancelled':
        return <Badge variant="neutral" data-id="element-19441">Отменен</Badge>;
    }
  };
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-19442">
      {/* 1. ВЕРХНЯЯ СВОДКА */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-19443">
        <Card className="p-5 border-slate-200" data-id="element-19444">
          <div className="flex items-center gap-3 mb-3" data-id="element-19445">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white shadow-sm`} data-id="element-19446">
              <Package className="w-5 h-5" data-id="element-19447" />
            </div>
            <div data-id="element-19448">
              <div className="text-sm font-medium text-slate-500" data-id="element-19449">
                Активные пакеты
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-19450">
                {activePackages.length}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-id="element-19451">
            Всего отчетов в пакетах:{' '}
            {packages.reduce((sum, p) => sum + p.totalReports, 0)}
          </p>
        </Card>

        <Card className="p-5 border-slate-200" data-id="element-19452">
          <div className="flex items-center gap-3 mb-3" data-id="element-19453">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm" data-id="element-19454">
              <CheckCircle2 className="w-5 h-5" data-id="element-19455" />
            </div>
            <div data-id="element-19456">
              <div className="text-sm font-medium text-slate-500" data-id="element-19457">
                Осталось отчетов
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-19458">
                {totalRemaining}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-id="element-19459">Готовы к использованию</p>
        </Card>

        <Card className="p-5 border-slate-200" data-id="element-19460">
          <div className="flex items-center gap-3 mb-3" data-id="element-19461">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm" data-id="element-19462">
              <History className="w-5 h-5" data-id="element-19463" />
            </div>
            <div data-id="element-19464">
              <div className="text-sm font-medium text-slate-500" data-id="element-19465">
                Использовано
              </div>
              <div className="text-2xl font-bold text-slate-900" data-id="element-19466">
                {totalUsed}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500" data-id="element-19467">Отчетов списано из пакетов</p>
        </Card>
      </div>

      {/* 2. СПИСОК ПАКЕТОВ */}
      <div className="flex items-center justify-between" data-id="element-19468">
        <h2 className="text-lg font-bold text-slate-900" data-id="element-19469">Мои пакеты</h2>
        <Button variant="primary" size="sm" onClick={() => onNavigate('pricing')} data-id="element-19470">
          Купить еще
        </Button>
      </div>

      {packages.length === 0 ? <Card className="p-12 text-center border-slate-200 border-dashed" data-id="element-19471">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-19472" />
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-19473">
            У вас пока нет пакетов
          </h3>
          <p className="text-slate-500 mb-6 max-w-md mx-auto" data-id="element-19474">
            Покупайте пакеты отчетов со скидкой до 30% и используйте их для
            оплаты любых услуг платформы.
          </p>
          <Button variant="primary" onClick={() => onNavigate('pricing')} data-id="element-19475">
            Выбрать пакет
          </Button>
        </Card> : <div className="space-y-4" data-id="element-19476">
          {packages.map(pkg => {
        const isExpanded = expandedPackageId === pkg.id;
        const progress = pkg.usedReports / pkg.totalReports * 100;
        return <Card key={pkg.id} className="border-slate-200 overflow-hidden transition-all duration-200 hover:shadow-md" data-id="element-19477">
                {/* Package Header (Always visible) */}
                <div className="p-5 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4" onClick={() => toggleExpand(pkg.id)} data-id="element-19478">
                  <div className="flex items-start gap-4" data-id="element-19479">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white shadow-sm flex-shrink-0`} data-id="element-19480">
                      <Package className="w-6 h-6" data-id="element-19481" />
                    </div>
                    <div data-id="element-19482">
                      <div className="flex items-center gap-2 mb-1" data-id="element-19483">
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-19484">
                          {pkg.title}
                        </h3>
                        {getStatusBadge(pkg.status)}
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-3" data-id="element-19485">
                        <span data-id="element-19486">Куплен: {formatDate(pkg.purchaseDate)}</span>
                        <span className="hidden sm:inline" data-id="element-19487">•</span>
                        <span className="hidden sm:inline" data-id="element-19488">
                          {formatPrice(pkg.purchasePrice)} (
                          {formatPrice(pkg.pricePerReport)} / шт)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:justify-end gap-6 md:w-1/3" data-id="element-19489">
                    <div className="flex-grow md:flex-grow-0 md:w-48" data-id="element-19490">
                      <div className="flex justify-between text-xs font-medium mb-1.5" data-id="element-19491">
                        <span className="text-slate-500" data-id="element-19492">
                          Использовано: {pkg.usedReports}
                        </span>
                        <span className="text-slate-900" data-id="element-19493">
                          Осталось: {pkg.remainingReports}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-19494">
                        <div className={`h-full bg-gradient-to-r ${theme.gradient} rounded-full transition-all duration-500`} style={{
                    width: `${progress}%`
                  }} data-id="element-19495" />
                      </div>
                    </div>
                    <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-lg hover:bg-slate-50" data-id="element-19496">
                      {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-19497" /> : <ChevronDown className="w-5 h-5" data-id="element-19498" />}
                    </button>
                  </div>
                </div>

                {/* Package Details (Expanded) */}
                {isExpanded && <div className="px-5 pb-5 pt-2 border-t border-slate-100 bg-slate-50/50" data-id="element-19499">
                    <div className="flex items-center justify-between mb-4" data-id="element-19500">
                      <h4 className="text-sm font-bold text-slate-900" data-id="element-19501">
                        История использования
                      </h4>
                      {pkg.remainingReports > 0 && <Button variant="outline" size="sm" onClick={() => onNavigate('services')} data-id="element-19502">
                          Оформить новый заказ
                        </Button>}
                    </div>

                    {pkg.deductions.length === 0 ? <div className="text-center py-6 bg-white rounded-xl border border-slate-200 border-dashed" data-id="element-19503">
                        <p className="text-sm text-slate-500" data-id="element-19504">
                          Вы еще не использовали отчеты из этого пакета
                        </p>
                      </div> : <div className="space-y-3" data-id="element-19505">
                        {pkg.deductions.map(deduction => <div key={deduction.id} className="flex items-center justify-between p-3 sm:p-4 bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-19506">
                            <div className="flex items-center gap-3" data-id="element-19507">
                              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0" data-id="element-19508">
                                {deduction.source === 'ai' ? <FileText className="w-4 h-4" data-id="element-19509" /> : <Video className="w-4 h-4" data-id="element-19510" />}
                              </div>
                              <div data-id="element-19511">
                                <div className="text-sm font-bold text-slate-900" data-id="element-19512">
                                  {deduction.reportTitle}
                                </div>
                                <div className="text-xs text-slate-500 flex items-center gap-2" data-id="element-19513">
                                  <span data-id="element-19514">{formatDate(deduction.date)}</span>
                                  <span data-id="element-19515">•</span>
                                  <span data-id="element-19516">Заказ #{deduction.orderId}</span>
                                  {deduction.astrologerName && <>
                                      <span data-id="element-19517">•</span>
                                      <span className="text-indigo-600" data-id="element-19518">
                                        {deduction.astrologerName}
                                      </span>
                                    </>}
                                </div>
                              </div>
                            </div>
                            <div className="text-sm font-bold text-rose-500 whitespace-nowrap" data-id="element-19519">
                              -1 отчёт
                            </div>
                          </div>)}
                      </div>}
                  </div>}
              </Card>;
      })}
        </div>}
    </div>;
}